import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Avatar, Typography, Divider, Tooltip, IconButton, Chip, useMediaQuery, useTheme,
  AppBar, Toolbar, BottomNavigation, BottomNavigationAction, Paper, Badge,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BarChartIcon from '@mui/icons-material/BarChart';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationBell from '../common/NotificationBell';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useAuth } from '../../context/AuthContext';
import { chatAPI } from '../../api';

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED = 72;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

const ROLE_COLORS = {
  admin: '#FF6B35', support_team: '#4472C4', team_member: '#2E7D32',
  qa_team: '#7B1FA2', higher_authority: '#C62828',
};
const ROLE_LABELS = {
  admin: 'Admin', support_team: 'Support Team', team_member: 'Team Member',
  qa_team: 'QA Team', higher_authority: 'Management',
};

export default function Sidebar() {
  const { user, logout, isSupportTeam, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  const refreshChatUnread = () => {
    chatAPI.getUnreadCount().then(res => setChatUnread(res.data?.count || 0)).catch(() => {});
  };

  // Live badge: fetch once, then keep it current via socket + on every route
  // change (covers messages just marked read after visiting the Chat page).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;
    refreshChatUnread();
    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'] });
    socket.on('chat:newMessage', refreshChatUnread);

    // Polling safety net: if the socket connection can't reach the server
    // (e.g. a reverse proxy not configured to upgrade WebSockets), this
    // keeps the badge honest anyway, just on a short delay instead of instant.
    const interval = setInterval(refreshChatUnread, 15000);

    return () => { socket.disconnect(); clearInterval(interval); };
    // eslint-disable-next-line
  }, [user?.id]);

  useEffect(() => {
    if (user) refreshChatUnread();
    // eslint-disable-next-line
  }, [location.pathname]);

  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', visible: true },
    {
      label: 'Chat',
      icon: (
        <Badge badgeContent={chatUnread} color="error" max={99} overlap="circular">
          <ChatIcon />
        </Badge>
      ),
      path: '/chat', visible: true,
    },
    { label: 'Projects',  icon: <FolderIcon />,    path: '/projects',  visible: true },
    { label: 'Status',    icon: <CheckCircleIcon />, path: '/status',  visible: true },
    { label: 'Defects',   icon: <BugReportIcon />,  path: '/defects',  visible: true },
    { label: 'Tickets',   icon: <ConfirmationNumberIcon />, path: '/tickets', visible: true },
    { label: 'Reports',   icon: <BarChartIcon />,   path: '/reports',  visible: true },
    { label: 'Leaves',    icon: <EventNoteIcon />,  path: '/leaves',   visible: true },
    { label: 'Categories', icon: <CategoryIcon />,  path: '/categories', visible: isSupportTeam() || isAdmin() },
    { label: 'Users',     icon: <PeopleIcon />,     path: '/users',    visible: isSupportTeam() || isAdmin() },
  ].filter(i => i.visible);

  // Bottom nav shows top 5 items on mobile
  const bottomNavItems = navItems.slice(0, 5);
  const currentBottomIdx = bottomNavItems.findIndex(i => location.pathname.startsWith(i.path));

  const DrawerContent = ({ onNavigate }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%',
      background: 'linear-gradient(180deg, #1A2F50 0%, #1E3A5F 60%, #162944 100%)' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: collapsed && !isMobile ? 'center' : 'space-between', minHeight: 52 }}>
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'linear-gradient(135deg, #3B82F6, #10B981)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.7rem' }}>PM</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1 }}>PM Tool</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem' }}>Project Manager</Typography>
            </Box>
          </Box>
        )}
        {!isMobile && (
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            {collapsed ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* User */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: ROLE_COLORS[user?.role] || '#4472C4', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>
          {user?.name?.charAt(0).toUpperCase()}
        </Avatar>
        {(!collapsed || isMobile) && (
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </Typography>
            <Chip label={ROLE_LABELS[user?.role] || user?.role} size="small"
              sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${ROLE_COLORS[user?.role]}30`, color: ROLE_COLORS[user?.role], border: `1px solid ${ROLE_COLORS[user?.role]}50` }} />
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 0.5 }} />

      {/* Nav */}
      <List sx={{ px: 1, py: 0.5, flex: 1, overflowY: 'auto' }}>
        {navItems.map(({ label, icon, path }) => {
          const active = location.pathname.startsWith(path);
          return (
            <Tooltip key={path} title={collapsed && !isMobile ? label : ''} placement="right">
              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton onClick={() => { navigate(path); if (isMobile) onNavigate?.(); }}
                  sx={{ borderRadius: 2, minHeight: 38, px: collapsed && !isMobile ? 1.5 : 2,
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                    borderLeft: active ? '3px solid #4FC3F7' : '3px solid transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, transition: 'all 0.15s' }}>
                  <ListItemIcon sx={{ color: active ? '#4FC3F7' : 'rgba(255,255,255,0.6)', minWidth: collapsed && !isMobile ? 0 : 40, justifyContent: 'center' }}>
                    {icon}
                  </ListItemIcon>
                  {(!collapsed || isMobile) && (
                    <ListItemText primary={label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 700 : 500, color: active ? '#fff' : 'rgba(255,255,255,0.7)' }} />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {isMobile && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <ListItem disablePadding sx={{ p: 1 }}>
            <ListItemButton onClick={logout}
              sx={{ borderRadius: 2, px: 2, justifyContent: 'flex-start', '&:hover': { bgcolor: 'rgba(255,100,100,0.15)' } }}>
              <ListItemIcon sx={{ color: 'rgba(255,150,150,0.8)', minWidth: 40, justifyContent: 'center' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', color: 'rgba(255,150,150,0.8)' }} />
            </ListItemButton>
          </ListItem>
        </>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile Top AppBar */}
        <AppBar position="fixed" sx={{ background: 'linear-gradient(90deg, #1A2F50, #1E3A5F)', boxShadow: '0 2px 12px rgba(0,0,0,0.3)', zIndex: 1300 }}>
          <Toolbar sx={{ minHeight: '56px !important', px: 2 }}>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ color: '#fff', mr: 1.5 }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: 'linear-gradient(135deg, #3B82F6, #10B981)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.62rem' }}>PM</Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, flex: 1 }}>PM Tool</Typography>
            <Avatar sx={{ width: 32, height: 32, bgcolor: ROLE_COLORS[user?.role], fontSize: '0.75rem', fontWeight: 700, mr: 1 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ color: '#fff' }}>
              <NotificationBell iconColor="rgba(255,255,255,0.7)" />
            </Box>
            <Tooltip title="Logout">
              <IconButton onClick={logout} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: 280, border: 'none' } }}>
          <DrawerContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>

        {/* Bottom Navigation */}
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300, borderTop: '1px solid', borderColor: 'divider' }} elevation={3}>
          <BottomNavigation value={currentBottomIdx} onChange={(_, v) => navigate(bottomNavItems[v].path)}
            sx={{ bgcolor: '#1E3A5F', '& .MuiBottomNavigationAction-root': { color: 'rgba(255,255,255,0.5)', minWidth: 0 },
              '& .Mui-selected': { color: '#4FC3F7' } }}>
            {bottomNavItems.map((item) => (
              <BottomNavigationAction key={item.path} label={item.label} icon={item.icon}
                sx={{ '& .MuiBottomNavigationAction-label': { fontSize: '0.6rem', fontWeight: 700 } }} />
            ))}
          </BottomNavigation>
        </Paper>
      </>
    );
  }

  // Desktop
  const width = collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH;
  return (
    <Drawer variant="permanent"
      sx={{ width, flexShrink: 0, '& .MuiDrawer-paper': { width, height: '100vh', overflowX: 'hidden', transition: 'width 0.25s ease', border: 'none', boxShadow: '4px 0 24px rgba(0,0,0,0.2)' } }}>
      <DrawerContent />
    </Drawer>
  );
}